import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import { Link } from "wouter";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { homepageProjects } from "@/data/projects";

export default function Portfolio() {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <section id="portfolio" className="relative py-24 md:py-32 bg-background overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow }}
          viewport={{ once: true }}
        >
          <div className={`${isRTL ? 'text-right' : ''}`}>
            <motion.span
              className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {isRTL ? "أعمالنا المميزة" : "Selected Work"}
            </motion.span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              <span className="gradient-text">{t("portfolioTitle")}</span>
            </h2>

            <p className="text-lg text-muted-foreground max-w-2xl mt-4 leading-relaxed">
              {t("portfolioDescription")}
            </p>
          </div>

          <Link href="/projects">
            <Button
              variant="outline"
              size="lg"
              className="group h-12 px-6 rounded-xl border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 shrink-0"
            >
              {t("viewAllProjects")}
              <ArrowRight className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 mr-2 ml-0' : 'ml-2'}`} />
            </Button>
          </Link>
        </motion.div>

        {/* Projects Grid - Bento Layout */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {homepageProjects.map((project) => (
            <motion.div
              key={project.id}
              variants={fadeInUp}
              className={`group ${project.featured ? 'md:row-span-2' : ''}`}
            >
              <Link href={`/projects/${project.id}`}>
                <div
                  className={`overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-primary/20 transition-all duration-500 cursor-pointer hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 flex flex-col ${
                    project.featured ? 'h-full' : ''
                  }`}
                >
                  {/* Image Section */}
                  <div className={`relative overflow-hidden ${project.featured ? 'flex-1 min-h-[280px]' : 'h-[220px]'}`}>
                    <img
                      src={project.image}
                      alt={t(project.title)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />

                    {/* Featured Badge */}
                    {project.featured && (
                      <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                        <span className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-full shadow-lg">
                          {isRTL ? "مميز" : "Featured"}
                        </span>
                      </div>
                    )}

                    {/* Category Badge */}
                    <div className={`absolute bottom-4 ${isRTL ? 'right-4' : 'left-4'}`}>
                      <span className="px-3 py-1.5 text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground rounded-full border border-border/50">
                        {project.tags[0]}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className={`p-6 md:p-7 flex flex-col gap-4 ${isRTL ? 'text-right' : ''}`}>
                    {/* Tags */}
                    <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : ''}`}>
                      {project.tags.slice(1).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2.5 py-1 rounded-md bg-muted/80 text-muted-foreground font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl md:text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight">
                      {t(project.title)}
                    </h3>

                    {/* Description */}
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">
                      {t(project.description)}
                    </p>

                    {/* Footer */}
                    <div className={`flex items-center justify-between pt-2 border-t border-border/40 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex items-center gap-2 text-primary font-semibold text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span>{t("viewProject")}</span>
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {isRTL ? project.durationAr : project.duration}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
